import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import { useHistory } from 'react-router'

import { FlexBox } from '../../../components/Base/FlexBox'
import { Text } from '../../../components/Text/Text'
import { formatDate } from '../../../helpers/formatters'
import { usePerformanceScore } from '../../../hooks/query/usePerformaceScore'
import { usePerformanceScoreRanges } from '../../../hooks/query/usePerformanceScoreRanges'
import { User } from '../../../models/user'
import { blueGray, lightGray1, primaryBlack, white } from '../../../theme/colors'
import { SafetyScoreContainer } from './OverviewSafetyScore'
import { OverviewSidebarScore } from './OverviewSidebarScore'
import { SafetyInfoTooltip } from './SafetyInfoTooltip'
import { SafetyScoreHeaderItem } from './SafetyScoreHeaderItem'
import { ScorePill } from './ScorePill'

interface OverviewPerformanceScoreProps {
  user?: User
  isHeader?: boolean
  isInsideSidebar?: boolean
}

export function OverviewPerformanceScore({ user, isHeader, isInsideSidebar }: OverviewPerformanceScoreProps) {
  const history = useHistory()
  const { performanceScore } = usePerformanceScore(user?.id ?? '', { enabled: !!user?.id })
  const { performanceScoreRanges } = usePerformanceScoreRanges()
  const onTimeServiceScore = performanceScore?.find((score) => score.eventType === 'On Time Service')
  const onTimePickupScore = performanceScore?.find((score) => score.eventType === 'On Time Pickup')
  const onTimeDeliveryScore = performanceScore?.find((score) => score.eventType === 'On Time Delivery')

  const openPerformanceScoreLink = () => {
    return history.push(`/compliance-safety-tool/${user?.id}#on-time-rate`)
  }

  if (isInsideSidebar) {
    return (
      <OverviewSidebarScore
        title="ON-TIME RATE"
        score={{
          score: onTimeServiceScore?.score,
          scoreColor: onTimeServiceScore?.scoreColor,
          scoreBackgroundColor: onTimeServiceScore?.scoreBackgroundColor,
          scoreLabel: onTimeServiceScore?.scoreLabel,
          isPercentage: true,
        }}
        tooltip={
          <SafetyInfoTooltip
            title="On-time rates are updated daily and reflect data from your most recent 20 jobs in the past 12 months."
            ranges={performanceScoreRanges}
          />
        }
        onClick={openPerformanceScoreLink}
      />
    )
  }

  return (
    <FlexBox
      isColumn
      gap="20px"
      mb={3}
      onClick={isHeader ? undefined : openPerformanceScoreLink}
      styles={`
        background-color: ${!isHeader ? white : null};
        padding: ${isHeader || isInsideSidebar ? '0' : '24px 32px'};
        border-radius: 2px;
      `}
    >
      <FlexBox justifyContent="space-between" alignItems="center">
        <FlexBox justifyContent="flex-start" gap="8px">
          <Text variant="uppercaseLabel3" mt={0.5}>
            ON-TIME RATE
          </Text>
          <SafetyInfoTooltip
            title="On-time rates are updated daily and reflect data from your most recent 20 jobs in the past 12 months."
            ranges={performanceScoreRanges}
          />
        </FlexBox>
        {!isHeader && <KeyboardArrowRightIcon htmlColor={blueGray} />}
      </FlexBox>

      <FlexBox gap="32px">
        <FlexBox isColumn gap="16px">
          <SafetyScoreContainer
            gap="16px"
            justifyContent="flex-start"
            alignItems="center"
            $isInsideSidebar={isInsideSidebar}
          >
            <ScorePill
              score={onTimeServiceScore?.score}
              color={onTimeServiceScore?.scoreColor}
              backgroundColor={onTimeServiceScore?.scoreBackgroundColor}
              isPercentage
            />
            <Text
              variant="header2"
              fontColor={onTimeServiceScore?.scoreColor ?? primaryBlack}
              mt={0.5}
              styles={isInsideSidebar ? 'font-size: 16px;' : 'font-size: 20px;'}
            >
              {onTimeServiceScore?.scoreLabel}
            </Text>
          </SafetyScoreContainer>

          <Text variant="label3" fontColor={lightGray1}>
            Updated on {onTimeServiceScore?.dateUpdated && formatDate(onTimeServiceScore?.dateUpdated)}
          </Text>
        </FlexBox>

        <FlexBox isColumn gap="8px" flex={isInsideSidebar ? 1 : undefined} style={{ alignSelf: 'start' }}>
          <SafetyScoreHeaderItem
            title="Pickup rate"
            color={onTimePickupScore?.scoreColor}
            label={onTimePickupScore?.scoreLabel}
            score={onTimePickupScore?.score}
          />

          <SafetyScoreHeaderItem
            title="Delivery rate"
            color={onTimeDeliveryScore?.scoreColor}
            label={onTimeDeliveryScore?.scoreLabel}
            score={onTimeDeliveryScore?.score}
          />
        </FlexBox>
      </FlexBox>
    </FlexBox>
  )
}
